import React, { useContext, useState } from 'react';
import _ from 'lodash';
import path from 'path';
import { useFormContext } from 'react-hook-form';
import { Button, Dialog, Typography, Divider } from '@mui/material';
import { NotificationContext } from '../../../../../NotificationContext';
import { downloadBulkChart, getFileURL, uploadBulkChart } from '../../../../../repository';
import { useFetchUsers, useUpdateUser } from '../../../../../data/generic-queries/users';
import { capitalize } from '../../../../../../utils';
import { ButtonSx } from '../../../../../main/apps/FunctionalApps/user-profile/common/utils';
import { tankFormConfig, getErrorMessage, getIndexByTankNumber, getTableColumns } from './TankDetails.helpers';
import { ActionColumn, Table, FormController, Dropzone } from '../../../../../custom-components';
import FormCreator from '../../form-creator/FormCreator';

const TankDetails = ({ props: { noTitle = false, queryKey = 'user-data' } = {} }) => {
    const { getValues } = useFormContext();
    const { showSnackbar } = useContext(NotificationContext);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [mode, setMode] = useState('add');
    const [editIndex, setEditIndex] = useState(null);

    const id = getValues('id') ?? getValues('_id');
    const { mutateAsync: mutateUser } = useUpdateUser({ key: queryKey });
    const { data, isLoading } = useFetchUsers({
        where: {
            _id: id,
        },
        enabled: !!id,
        key: queryKey,
    });
    const producer = data?.[0];
    const tanks = producer?.tanks;
    const bulkCharts = producer?.bulk_charts;

    const uploadNewBulkChart = async (files) => {
        if (!files.length) return;

        const type = _.lowerCase(path.extname(files[0].name));
        if (!['csv', 'xlsx'].includes(type)) {
            showSnackbar({
                severity: 'error',
                message: 'Please provide a valid .csv or .xlsx file.',
            });
            return;
        }

        const obj = {
            file: files[0],
            producer_id: id,
            bulk_tank: editIndex + 1,
        };

        const formData = new FormData();
        formData.append('file', obj.file);
        formData.append('producer_id', obj.producer_id);
        formData.append('bulk_tank', obj.bulk_tank);

        try {
            await uploadBulkChart(formData);
            showSnackbar({
                severity: 'success',
                message: 'Successfully updated bulk tank chart.',
            });
        } catch (error) {
            showSnackbar({
                severity: 'error',
                message: getErrorMessage('uploading', 'bulk tank chart'),
            });
        }
    };

    const handleFormSubmit = async (formValues) => {
        const idx = mode === 'edit' && editIndex != null ? editIndex : tanks.length;
        const updatedTanks = mode === 'edit' ? tanks.map((t, i) => (i === idx ? formValues : t)) : [...tanks, formValues];

        try {
            await mutateUser({
                id,
                data: {
                    tanks: updatedTanks,
                    number_of_tanks: updatedTanks.length,
                    ...(mode === 'add' ? { bulk_charts: [...(bulkCharts ?? []), ''] } : {}),
                },
            });
            showSnackbar({
                severity: 'success',
                message: `Tank ${mode === 'edit' ? 'updated' : 'created'} successfully.`,
            });
        } catch (error) {
            showSnackbar({
                severity: 'error',
                message: error?.message || getErrorMessage(mode === 'edit' ? 'updating' : 'creating', 'tank'),
            });
        } finally {
            setDialogOpen(false);
        }
    };

    const handleDeleteTank = async (idx) => {
        const updatedTanks = [...tanks];
        updatedTanks[idx] = null;

        const updatedCharts = [...bulkCharts];
        updatedCharts[idx] = '';

        try {
            await mutateUser({
                id,
                data: {
                    tanks: updatedTanks,
                    bulk_charts: updatedCharts,
                    number_of_tanks: updatedTanks.filter(Boolean).length,
                },
            });
            showSnackbar({ severity: 'success', message: 'Tank deleted.' });
        } catch (error) {
            showSnackbar({
                severity: 'error',
                message: error?.message || getErrorMessage('deleting', 'tank'),
            });
        }
    };

    const downloadChartTemplate = () => {
        getFileURL('BulkCharts/1/Bulk_Chart_Template.xlsx').then((result) => {
            window.open(result, '_blank');
        });
    };

    const handleDownloadBulkChart = async (idx) => {
        const bulkChartNumber = idx + 1;

        try {
            const result = await downloadBulkChart(id, bulkChartNumber);
            const file = await getFileURL(`BulkCharts/${bulkChartNumber}/${result.data}`);
            window.open(file, '_blank');
        } catch (error) {
            showSnackbar({ severity: 'error', message: error?.message || getErrorMessage('downloading', 'bulk tank chart') });
        }
    };

    const handleDeleteBulkChart = async (idx) => {
        const updatedCharts = [...bulkCharts];
        updatedCharts[idx] = '';

        try {
            await mutateUser({
                id,
                data: {
                    bulk_charts: updatedCharts,
                },
            });
            showSnackbar({ severity: 'success', message: 'Bulk chart successfully deleted.' });
        } catch (error) {
            showSnackbar({ severity: 'error', message: error?.message || getErrorMessage('deleting', 'bulk tank chart') });
        }
    };

    const openEditDialog = (idx) => {
        setMode('edit');
        setEditIndex(idx);
        setDialogOpen(true);
    };

    const openAddDialog = () => {
        setMode('add');
        setEditIndex(null);
        setDialogOpen(true);
    };

    const actionsColumn = ActionColumn({
        options: [
            {
                label: 'Edit Tank',
                action: (row) => {
                    const idx = getIndexByTankNumber(row?.tank_number, tanks);
                    if (idx >= 0) openEditDialog(idx);
                },
            },
            {
                label: 'Delete Tank',
                action: (row) => {
                    const idx = getIndexByTankNumber(row?.tank_number, tanks);
                    if (idx >= 0) handleDeleteTank(idx);
                },
            },
        ],
    });

    const columns = getTableColumns(tanks, bulkCharts, handleDownloadBulkChart, handleDeleteBulkChart);
    const rows = (tanks ?? []).filter(Boolean);

    if (isLoading) return <></>;

    return (
        <>
            {!noTitle && (
                <Typography variant="body1" fontWeight={600} sx={{ opacity: 0.8 }}>
                    TANK DETAILS
                </Typography>
            )}
            {tanks?.length ? (
                <>
                    <Table
                        columns={[actionsColumn, ...columns]}
                        getRowId={(row) => row.tank_number}
                        rows={rows}
                        hideFooter
                        PaperSx={{ marginBottom: 0, boxShadow: 'none' }} // wrap
                        GridSx={{ border: 0 }}
                    />
                    <Divider />
                </>
            ) : (
                <Typography variant="body2" sx={{ my: 1, fontWeight: 'bold' }}>
                    This producer does not have any tanks.
                </Typography>
            )}
            <div>
                <Button
                    variant="text"
                    sx={{ ...ButtonSx, marginTop: 2 }}
                    onClick={openAddDialog} // wrap
                >
                    Add Tank
                </Button>
            </div>

            <Dialog
                maxWidth="lg"
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                keepMounted
                PaperProps={{
                    onSubmit: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    },
                }}
            >
                <div style={{ padding: '16px', paddingLeft: '24px' }}>
                    <Typography variant="h5" fontWeight={600} paddingBottom={1}>
                        {capitalize(mode)} Tank
                    </Typography>
                    <FormController
                        key={mode === 'edit' ? `edit-${editIndex}` : 'add'}
                        defaultValues={mode === 'edit' && editIndex != null ? tanks?.[editIndex] ?? {} : {}}
                        submit={handleFormSubmit}
                        handleCancel={() => setDialogOpen(false)}
                        // wrap
                    >
                        <FormCreator config={tankFormConfig} isEdit={mode === 'edit'} />
                        {mode === 'edit' && (
                            <div style={{ marginTop: '16px' }}>
                                <Typography variant="h6" fontWeight={600}>
                                    Replace/Upload Chart
                                </Typography>
                                <Button
                                    variant="outlined"
                                    sx={{ ...ButtonSx, marginY: 2 }}
                                    onClick={downloadChartTemplate} // wrap
                                >
                                    Download Bulk Chart Template
                                </Button>
                                <Dropzone handleDrop={uploadNewBulkChart} message="Drag and drop your bulk chart here, or click to select files." />
                            </div>
                        )}
                    </FormController>
                </div>
            </Dialog>
        </>
    );
};

export default TankDetails;
